import React, { useEffect, useState } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { apiKeyAPI, vendorAPI } from '../lib/api';
import { toast } from 'sonner';
import { Plus, Key, Trash2, Loader2, CheckCircle2, XCircle, RefreshCw } from 'lucide-react';

export default function ApiKeysPage() {
  const [apiKeys, setApiKeys] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [testing, setTesting] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    vendor_code: '',
    api_key: '',
    base_url: '',
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [keysRes, vendorsRes] = await Promise.all([
        apiKeyAPI.list(),
        vendorAPI.list(),
      ]);
      setApiKeys(keysRes.data);
      setVendors(vendorsRes.data);
    } catch (error) {
      toast.error('Failed to load API keys');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.name || !formData.vendor_code || !formData.api_key) {
      toast.error('Please fill all required fields');
      return;
    }

    setCreating(true);
    try {
      await apiKeyAPI.create(formData);
      toast.success('API key added');
      setShowDialog(false);
      setFormData({ name: '', vendor_code: '', api_key: '', base_url: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add API key');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (keyId) => {
    if (!window.confirm('Delete this API key?')) return;
    
    try {
      await apiKeyAPI.delete(keyId);
      toast.success('API key deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleTest = async (keyId) => {
    setTesting(keyId);
    try {
      const res = await apiKeyAPI.test(keyId);
      if (res.data.is_valid) {
        toast.success('API key is valid');
      } else {
        toast.error('API key is invalid');
      }
      loadData();
    } catch (error) {
      toast.error('Test failed');
    } finally {
      setTesting(null);
    }
  };

  const getSelectedVendor = () => {
    return vendors.find(v => v.vendor_code === formData.vendor_code);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="api-keys-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight font-heading">API Keys</h1>
            <p className="text-muted-foreground mt-1">
              Manage your model vendor API keys
            </p>
          </div>
          <Dialog open={showDialog} onOpenChange={setShowDialog}>
            <DialogTrigger asChild>
              <Button data-testid="add-apikey-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add API Key
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add API Key</DialogTitle>
                <DialogDescription>
                  Add a new API key for a model vendor
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input
                    data-testid="apikey-name-input"
                    placeholder="e.g., Production OpenAI"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Vendor</Label>
                  <Select
                    value={formData.vendor_code}
                    onValueChange={(v) => {
                      const vendor = vendors.find(vd => vd.vendor_code === v);
                      setFormData({ 
                        ...formData, 
                        vendor_code: v,
                        base_url: vendor?.base_url || ''
                      });
                    }}
                  >
                    <SelectTrigger data-testid="apikey-vendor-select">
                      <SelectValue placeholder="Select vendor" />
                    </SelectTrigger>
                    <SelectContent>
                      {vendors.map((vendor) => (
                        <SelectItem key={vendor.vendor_code} value={vendor.vendor_code}>
                          {vendor.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>API Key</Label>
                  <Input
                    type="password"
                    data-testid="apikey-key-input"
                    placeholder="sk-..."
                    value={formData.api_key}
                    onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Base URL (Optional)</Label>
                  <Input
                    data-testid="apikey-baseurl-input"
                    placeholder={getSelectedVendor()?.base_url || 'https://api.openai.com/v1'}
                    value={formData.base_url}
                    onChange={(e) => setFormData({ ...formData, base_url: e.target.value })}
                  />
                  <p className="text-xs text-muted-foreground">
                    Leave empty to use default: {getSelectedVendor()?.base_url || '-'}
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
                <Button onClick={handleCreate} disabled={creating} data-testid="submit-apikey-btn">
                  {creating && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Add Key
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* API Keys Table */}
        <Card>
          <CardHeader>
            <CardTitle>Your API Keys</CardTitle>
            <CardDescription>
              API keys are encrypted and stored securely
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Key</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Base URL</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {apiKeys.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      <Key className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No API keys yet</p>
                      <p className="text-sm">Add your first API key to start fine-tuning</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  apiKeys.map((key) => (
                    <TableRow key={key.id} data-testid={`apikey-row-${key.id}`}>
                      <TableCell className="font-medium">{key.name}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">{key.vendor_code}</Badge>
                      </TableCell>
                      <TableCell className="font-mono text-sm">{key.masked_key}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {key.is_valid ? (
                            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                          ) : (
                            <XCircle className="w-4 h-4 text-red-500" />
                          )}
                          <span className="text-sm">{key.is_valid ? 'Valid' : 'Invalid'}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground truncate max-w-[200px]">
                        {key.base_url || 'Default'}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleTest(key.id)}
                          disabled={testing === key.id}
                          data-testid={`test-apikey-${key.id}`}
                        >
                          {testing === key.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <RefreshCw className="w-4 h-4" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(key.id)}
                          data-testid={`delete-apikey-${key.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="bg-zinc-50">
          <CardContent className="pt-6">
            <h3 className="font-medium mb-2">About API Key Security</h3>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• API keys are encrypted using AES-256 before storage</li>
              <li>• Keys are only decrypted when making requests to vendors</li>
              <li>• Platform administrators cannot view your API keys</li>
              <li>• You can delete keys at any time</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
